/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useEffect, useState } from 'react';

import { useLocation, useNavigate } from 'js/lib/useRouter';

import * as actions from 'bundles/authentication-common/components/actions';
import type { OnAuthenticationOptions } from 'bundles/authentication-common/components/actions';
import AuthenticationContentTracker from 'bundles/authentication-common/components/authenticationModal/AuthenticationContentTracker';
import CompleteSSO from 'bundles/authentication-common/components/authenticationModal/CompleteSSO';
import ExpiredLink from 'bundles/authentication-common/components/authenticationModal/ExpiredLink';
import ForgotPassword from 'bundles/authentication-common/components/authenticationModal/ForgotPassword';
import LinkedAccount from 'bundles/authentication-common/components/authenticationModal/LinkedAccount';
import Login from 'bundles/authentication-common/components/authenticationModal/Login';
import MagicLink from 'bundles/authentication-common/components/authenticationModal/MagicLink';
import OpenAIAuth from 'bundles/authentication-common/components/authenticationModal/OpenAI/OpenAIAuth';
import OrganizationSSOLogin from 'bundles/authentication-common/components/authenticationModal/OrganizationSSO/OrganizationSSOLogin';
import OrganizationSSOSignup from 'bundles/authentication-common/components/authenticationModal/OrganizationSSO/OrganizationSSOSignup';
import RequestMagicLink from 'bundles/authentication-common/components/authenticationModal/RequestMagicLink';
import SSO from 'bundles/authentication-common/components/authenticationModal/SSO';
import Signup from 'bundles/authentication-common/components/authenticationModal/Signup';
import UnifiedAuthEntry from 'bundles/authentication-common/components/authenticationModal/UnifiedAuthEntry';
import type SSOError from 'bundles/authentication-common/components/sso/SSOError';
import { AUTHENTICATION_MODAL_MODES } from 'bundles/authentication-common/constants';
import type { AUTHENTICATION_MODAL_MODES_KEY } from 'bundles/authentication-common/constants';
import { AuthModalTrackerContextProvider } from 'bundles/authentication-common/contexts/AuthenticationTracking';
import useEnterpriseSsoEmailCheck from 'bundles/authentication-common/hooks/useEnterpriseSsoCheck';
import useForceSSOLoginQuery from 'bundles/authentication-common/hooks/useForceSSOLoginQuery';
import useUserThirdPartyOrganizationsQuery from 'bundles/authentication-common/hooks/useUserThirdPartyOrganizationsQuery';
import useWhitelistOrgForSSO from 'bundles/authentication-common/hooks/useWhitelistOrgForSSO';
import { isTermsOfUseRolloutForUS } from 'bundles/authentication-common/utils';
import { getModalTrackerValue } from 'bundles/authentication-common/utils/eventingUtils';
import type { AuthenticationContentBaseProps } from 'bundles/user-account/types';

type Context = Record<string, any>;

export type Props = AuthenticationContentBaseProps & {
  context: Context;
  isStandaloneAuth?: boolean;
  hideOrganizationSSO?: boolean;
  preferredOrgIds?: string[];
};

const getAuthViews: (showProgressiveAuth: boolean) => Record<AUTHENTICATION_MODAL_MODES_KEY, any> = (
  showProgressiveAuth
) => ({
  complete: CompleteSSO,
  error: Login,
  expired: ExpiredLink,
  forgot: ForgotPassword,
  linked: LinkedAccount,
  login: showProgressiveAuth ? UnifiedAuthEntry : Login,
  logincontinue: Login,
  magiclink: MagicLink,
  organizationlogin: OrganizationSSOLogin,
  organizationsignup: OrganizationSSOSignup,
  requestmagiclink: RequestMagicLink,
  signup: showProgressiveAuth ? UnifiedAuthEntry : Signup,
  signupcontinue: Signup,
  sso: SSO,
  openaiauth: OpenAIAuth,
});

const styles = {
  container: css`
    max-height: 100%;
    height: 100vh;
    overflow: auto;
    position: relative;

    @media (min-width: 600px) {
      height: auto;
    }
  `,
};

const AuthenticationContent = ({
  context: contextFromProps,
  defaultMode = AUTHENTICATION_MODAL_MODES.LOGIN,
  isStandaloneAuth,
  redirectTo,
  ssoRedirectTo,
  magicLinkRedirect,
  preventRegister,
  switchFormsByQueryParam = true,
  hideOrganizationSSO = false,
  programId,
  preferredOrgIds,
}: Props) => {
  const [context, setContext] = useState<Context>(contextFromProps ?? {});
  const [ssoError, setSsoError] = useState<SSOError | undefined>();
  useEffect(() => {
    setContext((prevContext = {}) => {
      const filteredContext = Object.fromEntries(
        Object.entries(contextFromProps || {}).filter(([, value]) => value !== undefined)
      );
      return { ...prevContext, ...filteredContext };
    });
  }, [contextFromProps]);

  const [mode, setMode] = useState<AUTHENTICATION_MODAL_MODES_KEY>(defaultMode);
  const location = useLocation();
  const navigate = useNavigate();
  const { authProvider, showProgressiveAuth } = context;

  const { organizations, loading: isThirdPartyLoading } = useUserThirdPartyOrganizationsQuery(authProvider);

  const organizationId = organizations?.[0]?.id;
  const { forceSSOLogin, loading: isForceSSOLoading } = useForceSSOLoginQuery(organizationId || '');
  const { isOrgOrProgramWhiteListedForSSOLogin, isLoading: isWhitelistOrgForSSOLoginLoading } = useWhitelistOrgForSSO({
    thirdPartyOrganizationId: organizationId,
    programId,
  });

  // add epic variable for XDPs CRO
  const onNavigate = (
    mode: AUTHENTICATION_MODAL_MODES_KEY,
    options: Context = {},
    shouldSwitchFormsByQueryParam = switchFormsByQueryParam
  ) => {
    const areModesTheSame = location.pathname.includes(`/${mode}`);
    if (
      isStandaloneAuth &&
      !areModesTheSame &&
      (mode === AUTHENTICATION_MODAL_MODES.SIGNUP || mode === AUTHENTICATION_MODAL_MODES.LOGIN)
    ) {
      navigate(`/${mode}${location.search}`, { replace: true });
    } else {
      if (shouldSwitchFormsByQueryParam) {
        const searchParams = new URLSearchParams(location.search);
        searchParams.set('authMode', mode);
        navigate(`${location.pathname}?${searchParams.toString()}`, { replace: true });
      }
      setContext((prevContext) => ({ ...prevContext, ...options }));
      setMode(mode);
    }
  };

  const onLogin = (redirectURL?: string) => {
    actions.onLogin(redirectTo || redirectURL);
  };

  const onSignup = (redirectURL?: string, options?: OnAuthenticationOptions) => {
    actions.onSignup(redirectTo || redirectURL, options);
  };

  const onSSOError = (error: SSOError) => {
    const { code } = error;
    switch (code) {
      // For enforceSsoV2 - SSO v2 and loginDisabledForDomain, where we enforce enterprise sso login, we just set the error state and don't navigate
      case 'enforceSsoV2':
      case 'loginDisabledForDomain':
        return setSsoError(error);
      case 'accountLinkedToSocialAccount':
        return onNavigate(AUTHENTICATION_MODAL_MODES.LINKED, { accountType: error.response?.existingAccountType });
      case 'existingCourseraAccount':
      case 'noEmailAvailable':
      default:
        return onNavigate(AUTHENTICATION_MODAL_MODES.COMPLETE, {
          mode: code,
          referrer: mode,
          service: error.service,
        });
    }
  };

  const resetSsoError = () => {
    setSsoError(undefined);
  };

  const enterpriseSsoEmailCheckInfo = useEnterpriseSsoEmailCheck({
    preferredOrgIds,
    returnTo: ssoRedirectTo,
  });

  const onSSOSuccess = (isRegistration: boolean, hasSavedOnboardingOnSignup?: boolean) => {
    if (isRegistration) {
      onSignup(undefined, { addShowTouAcceptParam: isTermsOfUseRolloutForUS(), hasSavedOnboardingOnSignup });
    } else {
      onLogin();
    }
  };

  const ssoContext = {
    forceSSOLogin,
    organizations,
    shouldShowCustomSSO: isOrgOrProgramWhiteListedForSSOLogin,
    areAnyApiRequestsInFlight: isThirdPartyLoading || isForceSSOLoading || isWhitelistOrgForSSOLoginLoading,
    ssoError,
    resetSsoError,
    enterpriseSsoEmailCheckInfo,
    ssoRedirectTo,
  };

  const Content = (getAuthViews(showProgressiveAuth)[mode] || Login) as unknown as React.ComponentType<any>;
  return (
    <section css={styles.container} className="authentication-box-content">
      <AuthModalTrackerContextProvider
        value={getModalTrackerValue({
          mode,
          isProgressiveAuth: showProgressiveAuth,
          formVariant: context.formVariant,
          socialSsoService: context.thirdPartyAuthService,
        })}
      >
        <AuthenticationContentTracker />
        <Content
          redirect={redirectTo}
          magicLinkRedirect={magicLinkRedirect}
          navigate={onNavigate}
          onLogin={onLogin}
          onSignup={onSignup}
          onSSOError={onSSOError}
          onSSOSuccess={onSSOSuccess}
          preventRegister={preventRegister}
          hideOrganizationSSO={hideOrganizationSSO}
          {...ssoContext}
          {...context}
        />
      </AuthModalTrackerContextProvider>
    </section>
  );
};

export default AuthenticationContent;
